import { BRIEF_EXPORT_RETRY_AFTER_SECONDS } from '@sigora/domain';

import { ApiError, readApiError } from './errors.ts';
import { reportUnauthorized } from './unauthorized.ts';

/** The header the export names the version it captured in. */
const VERSION_HEADER = 'x-sigora-brief-version';

/** The name a PDF is saved under when the answer names none the browser can use. */
const FALLBACK_FILENAME = 'Brief.pdf';

/** A PDF of the Brief as the export answered it: the file, the name to save it under, its version. */
export interface BriefPdf {
  blob: Blob;
  filename: string;
  version: number;
}

/**
 * `brief_export_busy`: the server is making as many PDFs as it will, and says how long to wait
 * before asking again (`Retry-After`, in seconds; `BRIEF_EXPORT_RETRY_AFTER_SECONDS` when it names
 * none it can be read as).
 */
export class BriefExportBusyError extends ApiError {
  readonly retryAfterSeconds: number;

  constructor(error: ApiError, retryAfterSeconds: number) {
    super(error.code, error.message, error.status, error.detail, error.body);
    this.name = 'BriefExportBusyError';
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/** The seconds a `Retry-After` header names, or the export's own default for one that names none. */
export const retryAfterOf = (header: string | null): number => {
  const seconds = Number(header);
  return header !== null && header.trim() !== '' && Number.isInteger(seconds) && seconds >= 0
    ? seconds
    : BRIEF_EXPORT_RETRY_AFTER_SECONDS;
};

/**
 * The name `content-disposition` gives a file: its UTF-8 `filename*` where there is one, which
 * keeps the title's own letters, else its plain `filename`.
 */
export const filenameOf = (disposition: string | null): string => {
  if (disposition === null) {
    return FALLBACK_FILENAME;
  }
  const extended = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(disposition)?.[1];
  if (extended !== undefined) {
    try {
      return decodeURIComponent(extended.trim());
    } catch {
      // A malformed escape; the plain name below is the server's own fallback for it.
    }
  }
  const plain = /filename\s*=\s*"([^"]*)"/i.exec(disposition)?.[1];
  return plain === undefined || plain === '' ? FALLBACK_FILENAME : plain;
};

/**
 * Asks the server to take a snapshot of the study's current Brief and print it, and answers the
 * PDF. A refusal is thrown as the `ApiError` its body says, a 401 reported first so the app shows
 * the login view, and a busy server as a `BriefExportBusyError` naming how long to wait; an answer that is not a PDF naming its version — a proxy's page, say — is thrown
 * as an error of its own, since no file came back.
 */
export const downloadBriefPdf = async (
  studyId: string,
  signal?: AbortSignal,
): Promise<BriefPdf> => {
  const response = await fetch(`/api/studies/${studyId}/brief/export`, { method: 'POST', signal });

  if (!response.ok) {
    reportUnauthorized(response.status);
    const error = await readApiError(response);
    throw error.code === 'brief_export_busy'
      ? new BriefExportBusyError(error, retryAfterOf(response.headers.get('retry-after')))
      : error;
  }

  const type = response.headers.get('content-type') ?? '';
  const version = Number(response.headers.get(VERSION_HEADER));
  if (!type.startsWith('application/pdf') || !Number.isInteger(version) || version < 1) {
    throw new Error('the export did not answer a PDF of a Brief version');
  }

  return {
    blob: await response.blob(),
    filename: filenameOf(response.headers.get('content-disposition')),
    version,
  };
};
